import React from 'react';
import { User, Shield, CreditCard, Bell, Palette, FileText, Download, Calendar, Crown, Bot, Sparkles, Star, ArrowRight } from 'lucide-react';
import { AccountSettings } from './Settings/AccountSettings';
import { SubscriptionDetails } from './Settings/SubscriptionDetails';
import { SermonSettings } from './Settings/SermonSettings';
import { AISettings } from './Settings/AISettings';
import { PaymentButton } from './PaymentButton';
import { useSubscription } from '../hooks/useSubscription';
import { useSearchParams } from 'react-router-dom';
import { getActivePriceDisplay, COLLABORATOR_PLAN } from '../stripe-config';

const TAB_IDS = ['account', 'subscription', 'sermons', 'ai'] as const;
type TabId = (typeof TAB_IDS)[number];
const isTabId = (value: string | null): value is TabId => TAB_IDS.includes(value as TabId);

export function Settings() {
  // The active tab lives in the URL (?tab=subscription) so it can be deep-linked,
  // e.g. from an "upgrade your account" email to expired users.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = isTabId(tabParam) ? tabParam : 'account';
  const setActiveTab = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'account') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };
  const subscription = useSubscription();
  const { isCollaborator } = subscription;

   // Lead with the subscription tab when the user arrived via an upgrade link or has no active plan
   const shouldShowSubscriptionFirst = tabParam === 'subscription' || 
     (!subscription?.subscription?.active && !subscription?.stripeSubscription?.subscription_id);

  const tabs = [
    { id: 'account', name: 'Account', icon: User },
    { id: 'subscription', name: 'Subscription', icon: shouldShowSubscriptionFirst ? Crown : CreditCard },
    { id: 'sermons', name: 'Sermons', icon: FileText },
    { id: 'ai', name: 'AI Provider', icon: Bot },
  ];

  return (
    <div className="p-4 pb-24 md:p-8 md:pb-8">
      <h1 className="text-xl md:text-2xl font-bold text-midnight mb-6 md:mb-8">Settings</h1>

      {/* Collaborator upgrade banner */}
      {isCollaborator && (
        <div className="mb-6 bg-gradient-to-r from-electric/10 to-blue-50 border border-electric/20 rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="bg-electric/10 rounded-full p-2 shrink-0">
              <Star className="h-5 w-5 text-electric" />
            </div>
            <div>
              <div className="font-semibold text-midnight">You're on a Collaborator account</div>
              <div className="text-sm text-slate">You have access to shared sermons and AI tools. Upgrade to create your own sermon library, collections, and more.</div>
            </div>
          </div>
          <button
            onClick={() => setActiveTab('subscription')}
            className="flex items-center gap-2 px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 text-sm font-medium shrink-0"
          >
            Upgrade to Premium
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Mobile Tabs */}
      <div className="md:hidden mb-6">
        <select
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
          className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
        >
          <option value="account">Account</option>
          <option value="subscription">Subscription</option>
          <option value="sermons">Sermons</option>
          <option value="ai">AI Provider</option>
        </select>
      </div>

      <div className="flex flex-col md:flex-row md:space-x-8">
        {/* Desktop Sidebar */}
        <div className="hidden md:block w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-sm border border-sand/20 overflow-hidden">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full px-4 py-3 text-left flex items-center space-x-3 ${
                  activeTab === tab.id
                    ? 'bg-electric/10 text-electric border-r-2 border-electric'
                    : 'text-slate hover:bg-sand/10'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'account' && <AccountSettings />}
          {activeTab === 'subscription' && (
          <div className="space-y-6">
            {/* Collaborator: two-tier choice */}
            {isCollaborator && (
              <div className="space-y-4">
                {/* Collaborator Plan */}
                <div className="border border-electric/30 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="h-5 w-5 text-electric" />
                    <h2 className="text-lg font-bold text-midnight">Collaborator Plan</h2>
                    <span className="ml-auto text-xs bg-electric/10 text-electric px-2 py-0.5 rounded-full font-medium">Current Access Level</span>
                  </div>
                  <p className="text-sm text-slate mb-4">Keep your existing access — shared sermons, AI tools, and Preach Mode. No own library or sermon creation.</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-sand/10 rounded-lg p-4">
                      <div className="text-base font-semibold text-midnight mb-1">Monthly</div>
                      <div className="text-2xl font-bold text-midnight mb-3">
                        ${COLLABORATOR_PLAN.monthly.price}<span className="text-sm font-normal text-slate">/month</span>
                      </div>
                      <PaymentButton
                        interval="monthly"
                        priceId={COLLABORATOR_PLAN.monthly.priceId}
                        buttonText="Subscribe Monthly"
                        className="bg-electric text-white hover:bg-electric/90 font-medium"
                      />
                    </div>
                    <div className="bg-sand/10 rounded-lg p-4">
                      <div className="text-base font-semibold text-midnight mb-1">Annual</div>
                      <div className="text-2xl font-bold text-midnight mb-1">
                        ${COLLABORATOR_PLAN.annual.price}<span className="text-sm font-normal text-slate">/year</span>
                      </div>
                      <div className="text-xs text-slate mb-3">
                        Save ${COLLABORATOR_PLAN.monthly.price * 12 - COLLABORATOR_PLAN.annual.price} vs monthly
                      </div>
                      <PaymentButton
                        interval="annual"
                        priceId={COLLABORATOR_PLAN.annual.priceId}
                        buttonText="Subscribe Annually"
                        className="bg-electric text-white hover:bg-electric/90 font-medium"
                      />
                    </div>
                  </div>
                </div>

                {/* Full Premium */}
                <div className="bg-gradient-to-r from-electric to-blue-600 text-white p-6 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Crown className="h-5 w-5" />
                    <h2 className="text-lg font-bold">Full Premium</h2>
                    <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full font-medium">Recommended</span>
                  </div>
                  <p className="text-sm text-white/90 mb-4">Everything in Collaborator, plus your own sermon library, series, collections, and the full SermonBuild experience.</p>
                  {getActivePriceDisplay().isFounders && (
                    <div className="flex items-center mb-4 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                      <Sparkles className="h-4 w-4 mr-2 text-gold" />
                      <span className="font-semibold text-sm">Founders Pricing — Limited Time!</span>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                      <div className="text-base font-semibold mb-1">Monthly</div>
                      <div className="text-2xl font-bold mb-3">
                        ${getActivePriceDisplay().monthly}<span className="text-sm font-normal">/month</span>
                        {getActivePriceDisplay().isFounders && <span className="text-sm line-through opacity-60 ml-2">${getActivePriceDisplay().regularMonthly}</span>}
                      </div>
                      <PaymentButton
                        interval="monthly"
                        buttonText="Upgrade Monthly"
                        className="bg-white text-electric hover:bg-gray-100 font-medium"
                      />
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                      <div className="text-base font-semibold mb-1">Annual</div>
                      <div className="text-2xl font-bold mb-1">
                        ${getActivePriceDisplay().annual}<span className="text-sm font-normal">/year</span>
                        {getActivePriceDisplay().isFounders && <span className="text-sm line-through opacity-60 ml-2">${getActivePriceDisplay().regularAnnual}</span>}
                      </div>
                      <div className="text-sm mb-3 text-white/80">Save {getActivePriceDisplay().savingsPercent}%</div>
                      <PaymentButton
                        interval="annual"
                        buttonText="Upgrade Annually"
                        className="bg-white text-electric hover:bg-gray-100 font-medium"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Non-collaborator: show upgrade if no active paid subscription */}
            {!isCollaborator && !subscription?.subscription?.active && !subscription?.stripeSubscription?.subscription_id && (
              <div className="bg-gradient-to-r from-electric to-blue-600 text-white p-6 rounded-lg">
                <div className="flex items-center mb-4">
                  <Crown className="h-6 w-6 mr-3" />
                  <h2 className="text-xl font-bold">
                    {subscription?.isInTrial ? 'Upgrade Your Account' : 'Trial Expired - Upgrade to Continue'}
                  </h2>
                </div>
                <p className="mb-6 text-white/90">
                  {subscription?.isInTrial
                    ? 'Choose a subscription plan to unlock all SermonBuild features.'
                    : 'Your free trial has ended. Choose a subscription plan to continue using SermonBuild\'s premium features.'
                  }
                </p>

                {getActivePriceDisplay().isFounders && (
                  <div className="flex items-center mb-4 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                    <Sparkles className="h-4 w-4 mr-2 text-gold" />
                    <span className="font-semibold text-sm">Founders Pricing — Limited Time! Lock in this rate for life.</span>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <div className="text-lg font-semibold mb-2">Monthly Plan</div>
                    <div className="text-2xl font-bold mb-4">
                      ${getActivePriceDisplay().monthly}<span className="text-sm font-normal">/month</span>
                      {getActivePriceDisplay().isFounders && <span className="text-sm line-through opacity-60 ml-2">${getActivePriceDisplay().regularMonthly}</span>}
                    </div>
                    <PaymentButton
                      interval="monthly"
                      buttonText="Subscribe Monthly"
                      className="bg-white text-electric hover:bg-gray-100 font-medium"
                    />
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <div className="text-lg font-semibold mb-2">Annual Plan</div>
                    <div className="text-2xl font-bold mb-4">
                      ${getActivePriceDisplay().annual}<span className="text-sm font-normal">/year</span>
                      {getActivePriceDisplay().isFounders && <span className="text-sm line-through opacity-60 ml-2">${getActivePriceDisplay().regularAnnual}</span>}
                    </div>
                    <div className="text-sm mb-4 text-white/80">Save {getActivePriceDisplay().savingsPercent}% per year</div>
                    <PaymentButton
                      interval="annual"
                      buttonText="Subscribe Annually"
                      className="bg-white text-electric hover:bg-gray-100 font-medium"
                    />
                  </div>
                </div>
              </div>
            )}
            <SubscriptionDetails />
          </div>
        )}
          {activeTab === 'sermons' && <SermonSettings />}
          {activeTab === 'ai' && <AISettings />}
        </div>
      </div>
    </div>
  );
}